import { Popover as HeadlessPopover, PopoverPanel, PopoverButton, Transition } from "@headlessui/react";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react";
import clsx from "clsx";
import React, { Fragment, useEffect, useRef, useState, type JSX } from "react";

type PopoverChildren = React.ReactNode | ((closePopover: () => void) => React.ReactNode);

interface Props {
  ariaLabel?: string;
  className?: string;
  popoverClassName?: string;
  portal?: boolean;
  position?: "bottom";
  autoPosition?: boolean;
  interactionMode?: "hover" | "click";
  disabled?: boolean;
  label: React.ReactNode;
  children: PopoverChildren;
  onOpenChange?: (isOpen: boolean) => void;
}

interface OpenStateObserverProps {
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const OpenStateObserver = ({ isOpen, onOpenChange }: OpenStateObserverProps): null => {
  const onOpenChangeReference = useRef(onOpenChange);

  useEffect(() => {
    onOpenChangeReference.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    onOpenChangeReference.current?.(isOpen);
  }, [isOpen]);

  return null;
};

export const Popover = ({
  ariaLabel,
  className,
  popoverClassName,
  portal = false,
  position,
  autoPosition = false,
  interactionMode = "hover",
  disabled = false,
  label,
  children,
  onOpenChange,
}: Props): JSX.Element => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const shouldAutoPosition = autoPosition || portal;
  const { floatingStyles, refs } = useFloating({
    open: shouldAutoPosition && isOpen,
    placement: portal ? "bottom" : "bottom-start",
    strategy: "fixed",
    middleware: [
      offset(8),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${availableHeight}px`;
        },
      }),
    ],
    whileElementsMounted: shouldAutoPosition ? autoUpdate : undefined,
  });

  useEffect(() => {
    if (shouldAutoPosition) {
      refs.setReference(triggerRef.current);
    }
  }, [shouldAutoPosition, refs]);

  const handleOpenChange = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    onOpenChange?.(nextIsOpen);
  };

  const handlePointerEnter = (isOpen: boolean) => {
    if (!isOpen && triggerRef.current) {
      triggerRef.current.click();
    }
  };

  const handlePointerLeave = (isOpen: boolean, closePopover: () => void) => {
    if (isOpen) {
      closePopover();
    }
  };

  return (
    <HeadlessPopover className={clsx("relative", className)}>
      {({ open, close }) => {
        const popoverContent = typeof children === "function" ? children(close) : children;
        const hoverInteractionHandlers =
          interactionMode === "hover"
            ? {
                onMouseEnter: () => handlePointerEnter(open),
                onMouseLeave: () => handlePointerLeave(open, close),
              }
            : undefined;

        return (
          <div {...hoverInteractionHandlers}>
            <OpenStateObserver isOpen={open} onOpenChange={handleOpenChange} />

            <PopoverButton ref={triggerRef} className="focus:outline-0" aria-label={ariaLabel} disabled={disabled}>
              {label}
            </PopoverButton>

            <Transition
              as={Fragment}
              enter={
                shouldAutoPosition ? "transition-opacity ease-out duration-150" : "transition ease-out duration-150"
              }
              enterFrom={shouldAutoPosition ? "opacity-0" : "opacity-0 translate-y-1"}
              enterTo={shouldAutoPosition ? "opacity-100" : "opacity-100 translate-y-0"}
              leave={shouldAutoPosition ? "transition-opacity ease-in duration-150" : "transition ease-in duration-150"}
              leaveFrom={shouldAutoPosition ? "opacity-100" : "opacity-100 translate-y-0"}
              leaveTo={shouldAutoPosition ? "opacity-0" : "opacity-0 translate-y-1"}
            >
              <PopoverPanel
                portal={shouldAutoPosition}
                ref={shouldAutoPosition ? refs.setFloating : undefined}
                style={shouldAutoPosition ? floatingStyles : undefined}
                className={clsx(
                  shouldAutoPosition
                    ? clsx(
                        "z-50 overflow-y-auto rounded-lg bg-base-white p-1 font-normal shadow-md outline outline-2 outline-base-input lg:p-2",
                        portal ? "w-[50vw] max-w-[calc(100vw-2rem)]" : "w-max max-w-[min(70vw,31rem)]",
                      )
                    : [
                        "absolute right-0 z-10 w-max max-w-[min(70vw,31rem)]",
                        "translate-x-1/2 transform rounded-lg bg-base-white p-1 font-normal",
                        "shadow-md outline outline-2 outline-base-input lg:p-2",
                        position === "bottom" ? "top-[calc(100%_+_.5rem)]" : "bottom-[calc(100%_+_.5rem)]",
                      ],
                  popoverClassName,
                )}
              >
                {popoverContent}
              </PopoverPanel>
            </Transition>
          </div>
        );
      }}
    </HeadlessPopover>
  );
};
